import React from "react";

import {
	CalendarOutlined,
	IdcardOutlined,
	UserOutlined,
} from "@ant-design/icons";

import { calcAge, fmt, fmtDate, initials, phone } from "#utils";

/* ------------------------------------------------------------------ */
/*  Re-export helpers so existing imports keep working                  */
/* ------------------------------------------------------------------ */

export { calcAge, fmt, fmtDate, initials, phone };

/* ------------------------------------------------------------------ */
/*  Static config                                                      */
/* ------------------------------------------------------------------ */

export const caseColumns = [
	{ title: "Case #", dataIndex: "id", key: "id", width: 100 },
	{
		title: "Type",
		dataIndex: "medical_retail",
		key: "medical_retail",
		width: 100,
	},
	{
		title: "Medication",
		dataIndex: "medication_short_name",
		key: "medication_short_name",
		width: 150,
	},
	{ title: "Stage", dataIndex: "stage", key: "stage", minWidth: 120 },
	{ title: "Status", dataIndex: "status", key: "status", width: 120 },
	{
		title: "Opened",
		dataIndex: "created_on",
		key: "created_on",
		width: 120,
		render: fmtDate,
	},
];

export const headerActions = [];

/* ------------------------------------------------------------------ */
/*  Data builders (depend on patientData)                              */
/* ------------------------------------------------------------------ */

type P = Record<string, any>;

export const getHeaderMeta = (patientData: P) => [
	{
		icon: React.createElement(IdcardOutlined),
		label: "External ID:",
		value: fmt(patientData.external_id),
	},
	{
		icon: React.createElement(CalendarOutlined),
		label: "DOB:",
		value: `${fmtDate(patientData.dob)} (Age ${calcAge(patientData.dob)})`,
	},
	{ icon: React.createElement(UserOutlined), value: fmt(patientData.gender) },
];

export const getStatsData = (patientData: P) => [
	{ label: "Active Cases", value: fmt(patientData.active_cases_count, "0") },
	{ label: "Dispenses", value: fmt(patientData.dispense_count, "0") },
	{ label: "Days Enrolled", value: fmt(patientData.days_enrolled, "0") },
	{ label: "Adherence", value: `${fmt(patientData.adherence_pct, "0")}%` },
	{ label: "Open Tasks", value: fmt(patientData.open_tasks, "0") },
	{ label: "Last Contact", value: fmtDate(patientData.last_contact_date) },
];

export const getPlanDetailsRows = (patientData: P) => {
	const plan = patientData.plan_id as P | undefined;
	return [
		{ label: "Plan", value: fmt(plan?.name ?? plan?.short_name) },
		{
			label: "Go-Live",
			value: fmtDate(plan?.golive_date ?? patientData.golive_date),
		},
		{ label: "PBM", value: fmt(patientData.pbm_ids?.[0]?.name) },
		{
			label: "Medical Carrier",
			value: fmt(patientData.medical_carrier_ids?.[0]?.name),
		},
		{ label: "Group Status", value: fmt(patientData.group_status) },
	];
};
export const getMedicalCards = (patientData: P) => {
	const physician = patientData.physician_id as P | undefined;
	const address = [
		physician?.street,
		physician?.city,
		physician?.state_id,
		physician?.zip,
	]
		.filter(Boolean)
		.join(", ");

	return [
		{
			title: "Prescriber",
			rows: [
				{
					label: "PRIMARY PRESCRIBER",
					value: fmt(
						physician
							? `${physician.first} ${physician.last}`
							: undefined
					),
				},
				{
					label: "NPI NUMBER",
					value: fmt(physician?.npi_number),
				},
				{
					label: "PRESCRIBER PHONE",
					value: phone(physician?.phone),
				},
				{
					label: "FAX NUMBER",
					value: phone(physician?.fax),
				},
				{
					label: "FACILITY",
					value: fmt(physician?.facility_name),
				},
				{
					label: "ADDRESS",
					value: fmt(address || undefined),
				},
			],
		},
	];
};

export const getEnrollmentRows = (patientData: P) => [
	{ label: "Member Type", value: fmt(patientData.member_type) },
	{ label: "Onboarding Wizard", value: fmt(patientData.onboard_wizard) },
	{
		label: "App Onboarding Visit Completed",
		value: fmt(patientData.welcome_packet_received),
	},
	{
		label: "Member Opt-In/Opt-Out",
		value: fmt(patientData.member_enrollment),
	},
];
